import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { alertsAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Bell,
  AlertTriangle,
  Send,
  Users,
  TrendingDown,
  RefreshCw,
  Plus,
  CheckCircle,
  XCircle,
  Zap,
  Settings,
  MessageSquare,
  Award,
  Target,
} from 'lucide-react';
import { toast } from 'sonner';

// Alert message templates
const ALERT_TEMPLATES = {
  critical_behind: {
    title: "Urgent: Critical Performance Alert",
    message: "Your admission count ({current}) is significantly below your target ({target}). You need {remaining} more admissions to meet your goal. Please prioritize student follow-ups immediately.",
    type: "critical"
  },
  warning_behind: {
    title: "Performance Warning",
    message: "You're currently at {current} admissions against a target of {target}. You're {percentage}% behind schedule. Consider increasing your outreach efforts.",
    type: "warning"
  },
  approaching_target: {
    title: "Almost There!",
    message: "Great progress! You've achieved {current} admissions out of {target}. Just {remaining} more to go. Keep up the excellent work!",
    type: "info"
  },
  target_achieved: {
    title: "Congratulations! Target Achieved",
    message: "Outstanding! You've achieved your target of {target} admissions with {current} total. Your dedication is truly commendable!",
    type: "success"
  },
  weekly_reminder: {
    title: "Weekly Performance Reminder",
    message: "Weekly check-in: You have {current}/{target} admissions ({percentage}%). Keep pushing towards your goals!",
    type: "info"
  },
  low_activity: {
    title: "Activity Alert",
    message: "We noticed low activity this week. Remember, consistent effort leads to great results. Let's pick up the pace!",
    type: "warning"
  }
};

export default function GenerateAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Custom alert dialog
  const [customAlertDialog, setCustomAlertDialog] = useState(false);
  const [selectedCounselors, setSelectedCounselors] = useState([]);
  const [customAlert, setCustomAlert] = useState({
    title: '',
    message: '',
    type: 'warning'
  });
  const [sending, setSending] = useState(false);

  // Auto alert settings dialog
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [autoAlertSettings, setAutoAlertSettings] = useState({
    enableCritical: true,
    criticalThreshold: 30, // Below 30% of target
    enableWarning: true,
    warningThreshold: 50, // Below 50% of target
    enableEncouragement: true,
    encouragementThreshold: 80, // Above 80% of target
    enableWeeklyReminder: false
  });

  // Template dialog
  const [templateDialog, setTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [alertsRes, counselorsRes] = await Promise.all([
        alertsAPI.getTargetAlerts(),
        usersAPI.getAll()
      ]);
      setAlerts(alertsRes.data?.alerts || []);
      setCounselors(counselorsRes.data?.filter(u => u.role === 'counselor') || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load alerts data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateAutomatedAlerts = async () => {
    try {
      setGenerating(true);
      const res = await alertsAPI.generateTargetAlerts();
      
      // Count generated alerts by type
      const alertCounts = res.data?.alert_summary || {};
      
      let message = 'Automated alerts generated: ';
      if (alertCounts.critical > 0) message += `${alertCounts.critical} critical, `;
      if (alertCounts.warning > 0) message += `${alertCounts.warning} warnings, `;
      if (alertCounts.info > 0) message += `${alertCounts.info} info alerts`;
      
      toast.success(res.data?.message || message || 'Alerts generated successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to generate alerts:', error);
      toast.error('Failed to generate alerts');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendCustomAlert = async () => {
    if (!customAlert.title || !customAlert.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (selectedCounselors.length === 0) {
      toast.error('Please select at least one counselor');
      return;
    }

    try {
      setSending(true);
      await alertsAPI.sendCustomAlert({
        ...customAlert,
        counselor_ids: selectedCounselors
      });
      toast.success(`Alert sent to ${selectedCounselors.length} counselor(s)`);
      setCustomAlertDialog(false);
      resetCustomAlert();
      fetchData();
    } catch (error) {
      console.error('Failed to send alert:', error);
      toast.error('Failed to send alert');
    } finally {
      setSending(false);
    }
  };

  const resetCustomAlert = () => {
    setCustomAlert({ title: '', message: '', type: 'warning' });
    setSelectedCounselors([]);
  };

  const toggleCounselorSelection = (counselorId) => {
    setSelectedCounselors(prev => 
      prev.includes(counselorId)
        ? prev.filter(id => id !== counselorId)
        : [...prev, counselorId]
    );
  };

  const selectAllCounselors = () => {
    if (selectedCounselors.length === counselors.length) {
      setSelectedCounselors([]);
    } else {
      setSelectedCounselors(counselors.map(c => c.id));
    }
  };

  const selectByCriteria = (criteria) => {
    let selected = [];
    switch(criteria) {
      case 'behind':
        selected = alerts.filter(a => a.severity === 'critical' || a.severity === 'warning')
          .map(a => a.counselor_id);
        break;
      case 'ontrack':
        const behindIds = alerts.map(a => a.counselor_id);
        selected = counselors.filter(c => !behindIds.includes(c.id)).map(c => c.id);
        break;
      case 'critical':
        selected = alerts.filter(a => a.severity === 'critical').map(a => a.counselor_id);
        break;
      default:
        break;
    }
    setSelectedCounselors(selected);
  };

  const useTemplate = (templateKey) => {
    const template = ALERT_TEMPLATES[templateKey];
    if (template) {
      setCustomAlert({
        title: template.title,
        message: template.message,
        type: template.type
      });
      setTemplateDialog(false);
    }
  };

  const personalizeMessage = (message, counselor) => {
    const alert = alerts.find(a => a.counselor_id === counselor?.id);
    const current = alert?.current_count || 0;
    const target = alert?.target_count || 0;
    const remaining = Math.max(0, target - current);
    const percentage = target > 0 ? Math.round((current / target) * 100) : 0;
    
    return message
      .replace(/{current}/g, current)
      .replace(/{target}/g, target)
      .replace(/{remaining}/g, remaining)
      .replace(/{percentage}/g, percentage)
      .replace(/{name}/g, counselor?.name || 'Counselor');
  };

  const getSeverityBadge = (severity) => {
    const config = {
      critical: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
      warning: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: AlertTriangle },
      info: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Bell },
      success: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
    };
    const { color, icon: Icon } = config[severity] || config.info;
    return (
      <Badge className={`${color} flex items-center gap-1 border`}>
        <Icon className="h-3 w-3" />
        {severity}
      </Badge>
    );
  };

  const getProgressColor = (current, target) => {
    const percent = target > 0 ? (current / target) * 100 : 0;
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 80) return 'bg-blue-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Summary stats
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;
  const behindCount = alerts.length;
  const onTrackCount = counselors.length - behindCount;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#0F172A]">
              Generate Alerts
            </h1>
            <p className="text-sm text-[#475569] mt-1">
              Monitor counselor performance and send automated alerts
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => setSettingsDialog(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" onClick={() => setCustomAlertDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Custom Alert
            </Button>
            <Button
              onClick={generateAutomatedAlerts}
              disabled={generating}
              className="bg-[#0066CC] hover:bg-[#0052A3]"
              data-testid="generate-alerts-btn"
            >
              <Zap className={`h-4 w-4 mr-2 ${generating ? 'animate-pulse' : ''}`} />
              {generating ? 'Generating...' : 'Auto Generate Alerts'}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-[#475569]">Critical</p>
                  <p className="text-2xl font-bold text-red-600">{criticalCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-[#475569]">Warnings</p>
                  <p className="text-2xl font-bold text-yellow-600">{warningCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-[#475569]">On Track</p>
                  <p className="text-2xl font-bold text-green-600">{onTrackCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-[#475569]">Total Counselors</p>
                  <p className="text-2xl font-bold text-blue-600">{counselors.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alert Templates Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="h-5 w-5 text-[#0066CC]" />
              Quick Alert Templates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(ALERT_TEMPLATES).map(([key, template]) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCustomAlert({
                      title: template.title,
                      message: template.message,
                      type: template.type
                    });
                    setCustomAlertDialog(true);
                  }}
                  className="text-xs"
                >
                  {template.title.substring(0, 25)}...
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Performance Alerts Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#0066CC]" />
              Performance Alerts ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-12 text-[#475569]">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p className="font-medium text-green-600">All counselors are on track!</p>
                <p className="text-sm mt-1">No performance alerts at this time</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Counselor</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Current</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Auto Message</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert, index) => {
                    const remaining = (alert.target_count || 0) - (alert.current_count || 0);
                    const percentage = alert.target_count > 0 
                      ? Math.round((alert.current_count / alert.target_count) * 100) 
                      : 0;
                    
                    // Determine auto message based on performance
                    let autoMessage = '';
                    if (percentage < 30) {
                      autoMessage = `Critical: Need ${remaining} more admissions urgently`;
                    } else if (percentage < 50) {
                      autoMessage = `Warning: ${percentage}% of target achieved`;
                    } else if (percentage < 80) {
                      autoMessage = `On track but needs push: ${remaining} remaining`;
                    } else {
                      autoMessage = `Almost there! Just ${remaining} to go`;
                    }

                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#0066CC] flex items-center justify-center">
                              <span className="text-white text-sm font-medium">
                                {alert.counselor_name?.charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                            <span className="font-medium">{alert.counselor_name || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                        <TableCell>
                          <span className="font-medium">{alert.target_count || 0}</span>
                        </TableCell>
                        <TableCell>
                          <span className={percentage < 50 ? 'text-red-600 font-medium' : ''}>
                            {alert.current_count || 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${getProgressColor(alert.current_count, alert.target_count)}`}
                                style={{ width: `${Math.min(percentage, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-[#475569]">{percentage}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <p className="text-sm text-[#475569] truncate">{autoMessage}</p>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCounselors([alert.counselor_id]);
                              setCustomAlert({
                                title: percentage < 30 ? ALERT_TEMPLATES.critical_behind.title : ALERT_TEMPLATES.warning_behind.title,
                                message: personalizeMessage(
                                  percentage < 30 ? ALERT_TEMPLATES.critical_behind.message : ALERT_TEMPLATES.warning_behind.message,
                                  { id: alert.counselor_id, name: alert.counselor_name }
                                ),
                                type: percentage < 30 ? 'critical' : 'warning'
                              });
                              setCustomAlertDialog(true);
                            }}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Counselor Overview Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#0066CC]" />
              Counselor Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {counselors.map((counselor) => {
                  const alert = alerts.find(a => a.counselor_id === counselor.id);
                  const percentage = alert && alert.target_count > 0 
                    ? Math.round((alert.current_count / alert.target_count) * 100) 
                    : 100;
                  
                  return (
                    <div 
                      key={counselor.id} 
                      className={`p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                        alert?.severity === 'critical' ? 'border-red-300 bg-red-50' :
                        alert?.severity === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                        'border-green-300 bg-green-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#0066CC] flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-medium">
                            {counselor.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#0F172A] truncate">{counselor.name}</p>
                          <p className="text-xs text-[#475569]">{counselor.designation || 'Counselor'}</p>
                        </div>
                        {alert ? (
                          percentage < 50 ? (
                            <TrendingDown className="h-5 w-5 text-red-500" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-yellow-500" />
                          )
                        ) : (
                          <Award className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-[#475569]">Progress</span>
                          <span className="font-medium">
                            {alert ? `${alert.current_count}/${alert.target_count}` : 'On Track'}
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${getProgressColor(alert?.current_count || 100, alert?.target_count || 100)}`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Custom Alert Dialog */}
      <Dialog open={customAlertDialog} onOpenChange={(open) => {
        setCustomAlertDialog(open);
        if (!open) resetCustomAlert();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Alert to Counselors
            </DialogTitle>
            <DialogDescription>
              Compose and send a performance alert
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Alert Type */}
            <div>
              <Label>Alert Type</Label>
              <Select
                value={customAlert.type}
                onValueChange={(value) => setCustomAlert(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4 text-blue-500" />
                      Info
                    </div>
                  </SelectItem>
                  <SelectItem value="warning">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      Warning
                    </div>
                  </SelectItem>
                  <SelectItem value="critical">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-500" />
                      Critical
                    </div>
                  </SelectItem>
                  <SelectItem value="success">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Success / Encouragement
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Alert Title */}
            <div>
              <Label>Alert Title *</Label>
              <Input
                value={customAlert.title}
                onChange={(e) => setCustomAlert(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter alert title"
                className="mt-1"
              />
            </div>
            
            {/* Alert Message */}
            <div>
              <Label>Message *</Label>
              <Textarea
                value={customAlert.message}
                onChange={(e) => setCustomAlert(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Enter alert message. Use {current}, {target}, {remaining}, {percentage} for dynamic values."
                className="mt-1"
                rows={4}
              />
              <p className="text-xs text-[#475569] mt-1">
                Variables: {'{current}'}, {'{target}'}, {'{remaining}'}, {'{percentage}'}, {'{name}'}
              </p>
            </div>

            {/* Quick Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Select Recipients ({selectedCounselors.length} selected)</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={selectAllCounselors}>
                    {selectedCounselors.length === counselors.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </div>
              <div className="flex gap-2 mb-2">
                <Button variant="outline" size="sm" onClick={() => selectByCriteria('critical')}>
                  <XCircle className="h-3 w-3 mr-1 text-red-500" />
                  Critical Only
                </Button>
                <Button variant="outline" size="sm" onClick={() => selectByCriteria('behind')}>
                  <TrendingDown className="h-3 w-3 mr-1 text-yellow-500" />
                  Behind Target
                </Button>
                <Button variant="outline" size="sm" onClick={() => selectByCriteria('ontrack')}>
                  <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                  On Track
                </Button>
              </div>
              <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                {counselors.map((counselor) => {
                  const alert = alerts.find(a => a.counselor_id === counselor.id);
                  return (
                    <div 
                      key={counselor.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${
                        selectedCounselors.includes(counselor.id) ? 'bg-blue-50 border border-blue-200' : ''
                      }`}
                      onClick={() => toggleCounselorSelection(counselor.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCounselors.includes(counselor.id)}
                        onChange={() => {}}
                        className="rounded"
                      />
                      <span className="text-sm flex-1">{counselor.name}</span>
                      {alert && (
                        <Badge variant="outline" className="text-xs">
                          {alert.current_count}/{alert.target_count}
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomAlertDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendCustomAlert} 
              disabled={sending || selectedCounselors.length === 0}
              className="bg-[#0066CC] hover:bg-[#0052A3]"
            >
              <Send className="h-4 w-4 mr-2" />
              {sending ? 'Sending...' : `Send to ${selectedCounselors.length} Counselor(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Auto Alert Settings
            </DialogTitle>
            <DialogDescription>
              Configure automated alert thresholds
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Critical Alerts</p>
                <p className="text-sm text-[#475569]">Below {autoAlertSettings.criticalThreshold}% of target</p>
              </div>
              <Switch
                checked={autoAlertSettings.enableCritical}
                onCheckedChange={(checked) => 
                  setAutoAlertSettings(prev => ({ ...prev, enableCritical: checked }))
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Warning Alerts</p>
                <p className="text-sm text-[#475569]">Below {autoAlertSettings.warningThreshold}% of target</p>
              </div>
              <Switch
                checked={autoAlertSettings.enableWarning}
                onCheckedChange={(checked) => 
                  setAutoAlertSettings(prev => ({ ...prev, enableWarning: checked }))
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Encouragement Messages</p>
                <p className="text-sm text-[#475569]">Above {autoAlertSettings.encouragementThreshold}% of target</p>
              </div>
              <Switch
                checked={autoAlertSettings.enableEncouragement}
                onCheckedChange={(checked) => 
                  setAutoAlertSettings(prev => ({ ...prev, enableEncouragement: checked }))
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Weekly Reminders</p>
                <p className="text-sm text-[#475569]">Send weekly performance summaries</p>
              </div>
              <Switch
                checked={autoAlertSettings.enableWeeklyReminder}
                onCheckedChange={(checked) => 
                  setAutoAlertSettings(prev => ({ ...prev, enableWeeklyReminder: checked }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              toast.success('Settings saved');
              setSettingsDialog(false);
            }}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
